/**
 * Admin Portal — Dashboard page.
 * Platform-wide statistics with clickable drill-down cards.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography, Grid, Paper, Box, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Table, TableHead, TableRow, TableCell, TableBody, Chip, IconButton,
  Tabs, Tab,
} from '@mui/material';
import { People, Assessment, Security, Storage, Close, Delete } from '@mui/icons-material';
import { authFetch } from '../../../api/authFetch';
import PortalTooltip from '../../../components/PortalTooltip';
import RefreshButton from '../../../components/RefreshButton';

interface DetailData {
  title: string;
  items: any[];
  columns: { key: string; label: string }[];
}

const AdminDashboardPage: React.FC = () => {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState<DetailData | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [orgTab, setOrgTab] = useState(0); // 0=Active, 1=Inactive
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  const handleRemoveDomain = async (assignmentId: number) => {
    try {
      const res = await authFetch(`/api/admin/portal/domains/${assignmentId}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success && detailData) {
        setDetailData({
          ...detailData,
          items: detailData.items.filter((item: any) => item.id !== assignmentId),
        });
      }
    } catch {
      // silently ignore
    }
  };

  const fetchData = useCallback(() => {
    setLoading(true);
    setError(null);
    authFetch('/api/admin/stats')
      .then((r) => r.json())
      .then((data) => { if (data.success) setStats(data.stats); setLastRefreshed(new Date()); })
      .catch(() => setError('Failed to load admin stats'))
      .finally(() => { setLastRefreshed(new Date()); setLoading(false); });
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCardClick = async (cardType: string) => {
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      if (cardType === 'users') {
        const res = await authFetch('/api/admin/users');
        const data = await res.json();
        setDetailData({
          title: 'All Users',
          items: data.users || [],
          columns: [
            { key: 'name', label: 'Name' },
            { key: 'email', label: 'Email' },
            { key: 'role', label: 'Role' },
            { key: 'organization_domain', label: 'Organization' },
          ],
        });
      } else if (cardType === 'assessments') {
        const res = await authFetch('/api/admin/portal/dashboard');
        const data = await res.json();
        const domains = data.assigned_domains || [];
        setDetailData({
          title: 'Assigned Domains',
          items: domains,
          columns: [
            { key: 'domain_name', label: 'Domain' },
            { key: 'category', label: 'Category' },
            { key: 'assigned_to', label: 'Assigned To' },
            { key: 'status', label: 'Workflow Status' },
            { key: 'assigned_at', label: 'Assigned Date' },
          ],
        });
      } else if (cardType === 'roles') {
        const res = await authFetch('/api/admin/roles');
        const data = await res.json();
        setDetailData({
          title: 'Active Roles',
          items: data.roles || [],
          columns: [
            { key: 'display_name', label: 'Role' },
            { key: 'description', label: 'Description' },
            { key: 'user_count', label: 'Users' },
          ],
        });
      } else if (cardType === 'organizations') {
        const res = await authFetch('/api/admin/organizations');
        const data = await res.json();
        setOrgTab(0);
        setDetailData({
          title: 'Organizations',
          items: data.organizations || [],
          columns: [
            { key: 'name', label: 'Name' },
            { key: 'domain', label: 'Domain' },
            { key: 'user_count', label: 'Users' },
            { key: 'last_login', label: 'Last Login' },
            { key: 'is_active', label: 'Status' },
          ],
        });
      }
    } catch {
      setDetailData({ title: 'Error', items: [], columns: [] });
    } finally {
      setDetailLoading(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  const statCards = [
    { label: 'Total Users', value: stats?.users?.total || 0, icon: <People fontSize="large" />, color: '#00B8F1', type: 'users', tooltipKey: 'admin.dashboard.total_users' },
    { label: 'Assigned Domains', value: stats?.assessments?.assigned_domains || 0, icon: <Assessment fontSize="large" />, color: '#0097C7', type: 'assessments', tooltipKey: 'admin.dashboard.assigned_domains' },
    { label: 'Active Roles', value: stats?.roles?.total || 0, icon: <Security fontSize="large" />, color: '#007AA3', type: 'roles', tooltipKey: 'admin.dashboard.active_roles' },
    { label: 'Organizations', value: stats?.organizations?.total || 0, icon: <Storage fontSize="large" />, color: '#005F80', type: 'organizations', tooltipKey: 'admin.dashboard.organizations' },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Admin Dashboard
        </Typography>
        <RefreshButton onRefresh={fetchData} loading={loading} lastRefreshed={lastRefreshed} />
      </Box>

      <Grid container spacing={3}>
        {statCards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.label}>
            <PortalTooltip tooltipKey={card.tooltipKey}>
              <Paper
                elevation={2}
                onClick={() => handleCardClick(card.type)}
                sx={{
                  p: 3, textAlign: 'center', cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 },
                }}
              >
                <Box sx={{ color: card.color, mb: 1 }}>{card.icon}</Box>
                <Typography variant="h3" sx={{ fontWeight: 700, color: card.color }}>
                  {card.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {card.label}
                </Typography>
                <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                  Click for details →
                </Typography>
              </Paper>
            </PortalTooltip>
          </Grid>
        ))}
      </Grid>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {detailData?.title || 'Details'}
          <IconButton onClick={() => setDetailOpen(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : detailData && detailData.items.length > 0 ? (
            <>
              {/* Tabs for Organizations view */}
              {detailData.title === 'Organizations' && (
                <Tabs value={orgTab} onChange={(_, v) => setOrgTab(v)} sx={{ mb: 2 }}>
                  <Tab label={`Active (${detailData.items.filter((i) => i.is_active !== false).length})`} />
                  <Tab label={`Inactive (${detailData.items.filter((i) => i.is_active === false).length})`} />
                </Tabs>
              )}
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {detailData.columns.map((col) => (
                      <TableCell key={col.key} sx={{ fontWeight: 600 }}>{col.label}</TableCell>
                    ))}
                    {detailData.title === 'Assigned Domains' && (
                      <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(detailData.title === 'Organizations'
                    ? detailData.items.filter((item) => orgTab === 0 ? item.is_active !== false : item.is_active === false)
                    : detailData.items
                  ).map((item, idx) => (
                    <TableRow key={idx}>
                      {detailData.columns.map((col) => (
                        <TableCell key={col.key}>
                          {col.key === 'role' || col.key === 'status' ? (
                            <Chip label={item[col.key] || '—'} size="small" />
                          ) : col.key === 'is_active' ? (
                            <Chip
                              label={item[col.key] === false ? 'Inactive' : 'Active'}
                              size="small"
                              color={item[col.key] === false ? 'default' : 'success'}
                            />
                          ) : col.key === 'last_login' || col.key === 'created_at' || col.key === 'assigned_at' ? (
                            item[col.key]
                              ? new Date(item[col.key]).toLocaleString()
                              : '—'
                          ) : (
                            item[col.key] ?? '—'
                          )}
                        </TableCell>
                      ))}
                      {detailData.title === 'Assigned Domains' && (
                        <TableCell>
                          <IconButton
                            size="small"
                            color="error"
                            title="Remove assignment"
                            onClick={() => handleRemoveDomain(item.id)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          ) : (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              No data available
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDashboardPage;
